import {
  Component,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import { StyleSheet, View } from "@-/com.npmjs.react-native";
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import { APP_MODULE_LOAD_DEADLINE_MS } from "#app-module-load-deadline-ms";
import { whenApplicationVisualReady } from "#application-visual-ready";
import { revealBootsplash } from "#bootsplash-reveal";

interface AppModule {
  App: ComponentType;
}

/** First-render errors happen before effects can arm any JavaScript startup deadline. */
export class BootsplashRootErrorBoundary extends Component<
  { children: ReactNode; errorFallback: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(): void {
    // The recovery frame has committed. It must use primitives independent of the
    // failed application (particularly its asset resolver), then remove native cover.
    void revealBootsplash("app-mount-boundary");
  }

  render(): ReactNode {
    return this.state.failed ? this.props.errorFallback : this.props.children;
  }
}

export interface BootsplashAppMountBoundaryProps {
  errorFallback: ReactNode;
  fallback: ReactNode;
  loadApp: () => Promise<AppModule>;
}

const styles = StyleSheet.create({
  application: {
    flex: 1,
  },
  localShellOverlay: {
    bottom: 0,
    left: 0,
    position: "absolute",
    right: 0,
    top: 0,
  },
  root: {
    flex: 1,
  },
});

/**
 * The only native boot coordinator.
 *
 * This component intentionally accepts a loader instead of children. Passing children would stop
 * React from mounting the app, but it would not stop static imports from evaluating module-scope
 * SDK setup before the splash is hidden. The loader keeps the complete application graph outside
 * the pre-hide runtime graph.
 *
 * The native splash comes down only once a routed destination has committed its visible frame
 * (`whenApplicationVisualReady`), never merely because this boundary mounted — or, failing open,
 * once that signal or the module load has outlived its deadline (reported, never silent). The local fallback
 * continues the splash artwork without duplicating navigation. Learn's local card and controls
 * commit before the handoff, including when its data is still loading.
 */
export function BootsplashAppMountBoundary({
  errorFallback,
  fallback,
  loadApp,
}: BootsplashAppMountBoundaryProps) {
  const [App, setApp] = useState<ComponentType | null>(null);
  const [applicationVisualReady, setApplicationVisualReady] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const loadAppPromiseRef = useRef<Promise<AppModule> | null>(null);
  // Expo Router rebuilds route component identities when its root is rendered again.
  // Removing the boot shell must preserve the already-committed router, providers,
  // query cache and route state. Context changes still reach their consumers normally.
  const application = useMemo(() => (App === null ? null : <App />), [App]);

  useEffect(() => {
    let active = true;

    const mountApp = async (): Promise<void> => {
      try {
        const loading = (loadAppPromiseRef.current ??= loadApp());
        // Mount on arrival — including a module that arrives after the deadline below has already
        // shown the error surface — and then hand off once the chrome commits. The chrome signal
        // fails open after `APPLICATION_VISUAL_READY_DEADLINE_MS`: a route that never mounts the
        // marker reveals late instead of never.
        loading.then(
          async (module) => {
            if (!active) {
              return;
            }
            setApp(() => module.App);
            await whenApplicationVisualReady();
            if (active) {
              setApplicationVisualReady(true);
            }
          },
          () => {
            // Surfaced through the bounded await below.
          },
        );
        await withDeadline(
          { deadline: APP_MODULE_LOAD_DEADLINE_MS, operation: "application module load" },
          () => loading,
        );
      } catch {
        if (active) {
          setLoadFailed(true);
        }
        // The effect below reveals only after the independent error frame commits.
      }
    };

    void mountApp();

    return () => {
      active = false;
    };
  }, [loadApp]);

  // The reveal runs from a passive effect on the render that DROPPED the shell, so the native
  // cross-dissolve is guaranteed to start over committed real chrome. Doing it inline in the async
  // flow above hides the splash in the same task as the state update, and the fade then plays over
  // the placeholder shell — the defect this boundary exists to prevent.
  useEffect(() => {
    if (!applicationVisualReady && !loadFailed) {
      return;
    }
    void revealBootsplash("app-mount-boundary");
  }, [applicationVisualReady, loadFailed]);

  if (App === null) {
    return loadFailed ? errorFallback : fallback;
  }

  return (
    <View style={styles.root}>
      <View style={styles.application}>{application}</View>
      {applicationVisualReady ? null : (
        <View pointerEvents="none" style={styles.localShellOverlay}>
          {fallback}
        </View>
      )}
    </View>
  );
}
